<script setup lang="ts">
import { Skeleton, Icon, Markdown, Rating } from "@/components/Ui";
import { TReviewItemType } from "@/types/review.types";
import { computed } from "vue";

interface Props {
  itemId: number;
  isFavoriteBlock?: boolean;
}

const { $getFormatDate } = useNuxtApp();
const props = defineProps<Props>();
const { t } = useI18n();
const params = {
  id: props.itemId,
  fields: ["title", "date", "text", "rating", "url", "user", "favorite"],
  "populate[review_platform][fields][0]": ["slug", "title"],
};
const { pending, data } = await useLazyFetch("/api/review-items", { params });
const item = computed(() => {
  return data.value?.data?.attributes;
});
</script>

<template>
  <a
    :href="item?.url"
    target="_blank"
    rel="nofollow noopener"
    :class="`${$style.root} ${
      item?.favorite && props.isFavoriteBlock ? $style['root-favorite'] : ''
    }`"
  >
    <div :class="$style['root-body']">
      <div :class="$style.user">
        <Skeleton
          :pending="pending"
          :options="{ width: 44, height: 44 }"
        >
          <template #loader>
            <circle
              cx="22"
              cy="22"
              r="22"
            />
          </template>
          <div :class="$style['user-icon']">
            <nuxt-img
              :src="`/img/review-platform/${item?.review_platform?.data?.attributes?.slug}-circle.svg`"
              loading="lazy"
            />
          </div>
        </Skeleton>
        <div :class="$style['user-info']">
          <span :class="$style['user-name']">
            <Skeleton
              :pending="pending"
              :options="{ width: 100, height: 19.8 }"
            >
              <template #loader>
                <rect
                  x="0"
                  y="0"
                  rx="6"
                  ry="6"
                  width="100"
                  height="19.8"
                />
              </template>
              {{ item.user }}
            </Skeleton>
          </span>
          <Skeleton
            :pending="pending"
            :options="{ width: 70, height: 18 }"
          >
            <template #loader>
              <rect
                x="0"
                y="0"
                rx="6"
                ry="6"
                width="70"
                height="15"
              />
            </template>
            <span :class="$style['user-platform']">
              {{
                t("reviews.on", {
                  p: item?.review_platform?.data?.attributes?.title,
                })
              }}
            </span>
          </Skeleton>
        </div>
      </div>
      <div :class="$style.date">
        <Skeleton
          :pending="pending"
          :options="{ width: 69, height: 18 }"
        >
          <template #loader>
            <rect
              x="0"
              y="0"
              rx="8"
              ry="8"
              width="69"
              :height="18"
            />
          </template>
          <template v-if="item?.date">
            <client-only>
              {{ $getFormatDate(item.date, $i18n.locale, "d MMM yyyy") }}
            </client-only>
          </template>
        </Skeleton>
      </div>
      <div :class="$style.rating">
        <Skeleton
          :pending="pending"
          :options="{ width: 140, height: 24.89 }"
        >
          <template #loader>
            <rect
              x="0"
              y="0"
              rx="6"
              ry="6"
              width="24"
              height="24"
            />
            <rect
              x="28"
              y="0"
              rx="6"
              ry="6"
              width="24"
              height="24"
            />
            <rect
              x="56"
              y="0"
              rx="6"
              ry="6"
              width="24"
              height="24"
            />
            <rect
              x="84"
              y="0"
              rx="6"
              ry="6"
              width="24"
              height="24"
            />
            <rect
              x="112"
              y="0"
              rx="6"
              ry="6"
              width="24"
              height="24"
            />
          </template>
          <template v-if="item?.rating">
            <Rating
              star-size="24"
              :model-value="item?.rating"
            />
          </template>
        </Skeleton>
      </div>
      <div :class="$style.desc">
        <div :class="$style.title">
          <Skeleton
            :pending="pending"
            :options="{ width: 240, height: 21.59 }"
          >
            <template #loader>
              <rect
                x="0"
                y="0"
                rx="8"
                ry="8"
                width="240"
                :height="21.59"
              />
            </template>
            <template v-if="item?.title">
              {{ item.title }}
            </template>
          </Skeleton>
        </div>
        <div :class="$style.text">
          <Skeleton
            :pending="pending"
            :options="{ width: 320, height: 67.5 }"
          >
            <template #loader>
              <rect
                x="0"
                y="0"
                rx="8"
                ry="8"
                width="320"
                height="18"
              />
              <rect
                x="0"
                y="24"
                rx="8"
                ry="8"
                width="280"
                height="18"
              />
              <rect
                x="0"
                y="48"
                rx="8"
                ry="8"
                width="240"
                height="18"
              />
            </template>
            <Markdown
              :content="
                props.isFavoriteBlock && item.text.length > 180
                  ? item.text.substring(0, 210) + '...'
                  : item.text
              "
            />
          </Skeleton>
        </div>
      </div>
    </div>
  </a>
</template>

<style lang="scss" module>
.root {
  @apply p-5 rounded-[12px] w-full;
  @apply bg-neutral-100 hover:bg-neutral-200 hover:bg-opacity-50;
  @apply min-h-[264px];

  &-body {
    @apply flex flex-wrap items-start gap-4;
  }

  &-favorite {
    &:hover {
      @apply scale-[0.98] duration-500;
    }
  }
}

.user {
  @apply flex items-center gap-3 w-fit;

  &-icon {
    @apply w-[44px] h-[44px] rounded-full overflow-hidden;

    & img {
      @apply w-full h-full rounded-full;
    }
  }

  &-info {
    @apply flex flex-col gap-1;
  }

  &-name {
    @apply text-[18px] font-semibold leading-[1.1];
  }

  &-platform {
    @apply text-sm font-medium;
  }
}

.date {
  @apply text-sm w-full uppercase font-medium md:w-fit md:ml-auto;
}

.rating {
  @apply flex justify-start w-full;
}

.desc {
  @apply flex flex-col gap-2 w-full;
}

.title {
  @apply font-semibold w-full leading-[1.2] text-[18px];
}

.text {
  @apply text-neutral-700 w-full text-[15px];

  & p {
    @apply flex flex-col gap-1 mb-3;

    &:last-of-type {
      @apply mb-0;
    }
  }
}
</style>
